import { CalendarIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../../ui/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../ui/components/ui/popover';
import { FilterMetaYearResponseDto } from '../../types/response';

interface KpiWeekRangeSelectorComponentProps {
  years: FilterMetaYearResponseDto[];
  currentFromYear: number;
  currentFromWeek: number;
  currentToYear: number;
  currentToWeek: number;
  disabled?: boolean;
  onChange: (range: { from: { week: string; year: string }; to: { week: string; year: string } }) => void;
}

interface WeekPickerProps {
  label: string;
  years: FilterMetaYearResponseDto[];
  pendingYear: number;
  pendingWeek: number | null;
  open: boolean;
  onYearSelect: (year: number) => void;
  onWeekSelect: (week: number) => void;
  isWeekDisabled?: (week: number, year: number) => boolean;
}

const WeekPicker = ({ label, years, pendingYear, pendingWeek, open, onYearSelect, onWeekSelect, isWeekDisabled }: WeekPickerProps) => {
  const activeMonthRef = useRef<HTMLDivElement>(null);
  const months = years.find((y) => y.year === pendingYear)?.months ?? [];

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        activeMonthRef.current?.scrollIntoView({ block: 'nearest' });
      }, 0);
    }
  }, [open]);

  return (
    <div className="space-y-3 flex-1">
      <p className="text-xs font-semibold text-[#12282A]">{label}</p>

      {/* Year */}
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground">Jahr</p>
        <div className="flex flex-wrap gap-1.5">
          {years.map(({ year }) => (
            <Button
              key={year}
              size="sm"
              variant={pendingYear === year ? 'default' : 'outline'}
              className="h-7 px-2.5 text-xs"
              onClick={() => onYearSelect(year)}>
              {year}
            </Button>
          ))}
        </div>
      </div>

      {/* Weeks grouped by month */}
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground">
          Kalenderwoche
          {pendingWeek === null && <span className="ml-1 text-destructive">– bitte wählen</span>}
        </p>
        <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
          {months.map(({ month, title, weeks }) => {
            const isActiveMonth = pendingWeek !== null && weeks.includes(pendingWeek);
            return (
              <div key={month} ref={isActiveMonth ? activeMonthRef : null}>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">{title}</p>
                <div className="flex flex-wrap gap-1">
                  {weeks.map((week) => {
                    const disabled = isWeekDisabled?.(week, pendingYear) ?? false;
                    return (
                      <Button
                        key={week}
                        size="sm"
                        variant={pendingWeek === week ? 'default' : 'ghost'}
                        className="h-7 px-2 text-xs"
                        disabled={disabled}
                        onClick={() => onWeekSelect(week)}>
                        KW {week}
                      </Button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const KpiWeekRangeSelectorComponent = ({
  years,
  currentFromYear,
  currentFromWeek,
  currentToYear,
  currentToWeek,
  disabled,
  onChange
}: KpiWeekRangeSelectorComponentProps) => {
  const [open, setOpen] = useState(false);

  const [fromYear, setFromYear] = useState<number>(currentFromYear);
  const [fromWeek, setFromWeek] = useState<number | null>(currentFromWeek);
  const [toYear, setToYear] = useState<number>(currentToYear);
  const [toWeek, setToWeek] = useState<number | null>(currentToWeek);

  useEffect(() => {
    setFromYear(currentFromYear);
    setFromWeek(currentFromWeek);
    setToYear(currentToYear);
    setToWeek(currentToWeek);
  }, [currentFromYear, currentFromWeek, currentToYear, currentToWeek]);

  const isToWeekDisabled = (week: number, year: number) => {
    if (fromWeek === null) return false;
    if (year > fromYear) return false;
    if (year < fromYear) return true;
    return week < fromWeek;
  };

  const handleFromYearSelect = (year: number) => {
    setFromYear(year);
    setFromWeek(year === currentFromYear ? currentFromWeek : null);
    if (toWeek !== null) {
      const fromAfterTo = year > toYear || (year === toYear && (fromWeek ?? 0) > (toWeek ?? 0));
      if (fromAfterTo) {
        setToYear(year);
        setToWeek(null);
      }
    }
  };

  const handleFromWeekSelect = (week: number) => {
    setFromWeek(week);
    if (toWeek !== null && (fromYear > toYear || (fromYear === toYear && week > toWeek))) {
      setToWeek(null);
    }
  };

  const handleToYearSelect = (year: number) => {
    setToYear(year);
    setToWeek(year === currentToYear ? currentToWeek : null);
  };

  const canConfirm = fromWeek !== null && toWeek !== null;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onChange({
      from: { week: String(fromWeek), year: String(fromYear) },
      to: { week: String(toWeek), year: String(toYear) }
    });
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setFromYear(currentFromYear);
      setFromWeek(currentFromWeek);
      setToYear(currentToYear);
      setToWeek(currentToWeek);
    }
    setOpen(next);
  };

  const triggerLabel = `${currentFromYear} | KW ${currentFromWeek} – ${currentToYear} | KW ${currentToWeek}`;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled} className="gap-2 text-xs h-8">
          <CalendarIcon className="h-4 w-4" />
          {triggerLabel}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[640px] p-4 space-y-4" align="start">
        <div className="flex gap-6">
          <WeekPicker
            label="Von"
            years={years}
            pendingYear={fromYear}
            pendingWeek={fromWeek}
            open={open}
            onYearSelect={handleFromYearSelect}
            onWeekSelect={handleFromWeekSelect}
          />

          <div className="w-px bg-[#E4E7EC]" />

          <WeekPicker
            label="Bis"
            years={years}
            pendingYear={toYear}
            pendingWeek={toWeek}
            open={open}
            onYearSelect={handleToYearSelect}
            onWeekSelect={(week) => setToWeek(week)}
            isWeekDisabled={isToWeekDisabled}
          />
        </div>

        <Button className="w-full" size="sm" disabled={!canConfirm} onClick={handleConfirm}>
          Übernehmen
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export default KpiWeekRangeSelectorComponent;
